import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { PageErrorFragment } from "@saleor/fragments/types/PageErrorFragment";
import { PageheaderDetailFragment } from "@saleor/fragments/types/PageheaderDetailFragment";
import { sectionNames } from "@saleor/intl";
import { maybe, splitDateTime } from "@saleor/misc";
import { PageHeaderLocatedEnum } from "@saleor/types/globalTypes";
import { concat } from "lodash-es";
import React from "react";
import { useIntl } from "react-intl";

import PageheaderDates from "../PageheaderDates";
import PageheaderDetails from "../PageheaderDetails/PageheaderDetails";
import PageheaderSummary from "../PageheaderSummary";

export interface PageheaderPageFormData {
  name: string;
  isDefault: boolean;
  isTop: boolean;
  countdown: boolean;
  pcCountdown: boolean;
  mbCountdown: boolean;
  languageOpt: boolean;
  pcContent: string;
  mobileContent: string;
  pcContentFormatted: string;
  mobileContentFormatted: string;
  located: PageHeaderLocatedEnum;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  mainColor: string;
  secondaryColor: string;
  pcRollingDuration: number;
  mbRollingDuration: number;
}

export interface PageheaderPageProps {
  pageheader: PageheaderDetailFragment | null;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: PageheaderPageFormData) => void;
  errors: PageErrorFragment[];
  title: string;
}

const PageheaderCreatePage: React.FC<PageheaderPageProps> = ({
  pageheader,
  disabled,
  errors,
  saveButtonBarState,
  onBack,
  onDelete,
  onSubmit,
  title
}) => {
  const intl = useIntl();
  const initialForm: PageheaderPageFormData =
    pageheader === null
      ? {
          name: "",
          isDefault: false,
          isTop: false,
          countdown: false,
          pcCountdown: false,
          mbCountdown: false,
          languageOpt: false,
          pcContent: "",
          mobileContent: "",
          pcContentFormatted: "-",
          mobileContentFormatted: "-",
          located: PageHeaderLocatedEnum.TOP,
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          mainColor: "",
          secondaryColor: "",
          pcRollingDuration: 0,
          mbRollingDuration: 0
        }
      : {
          name: maybe(() => pageheader.name, ""),
          isDefault: maybe(() => pageheader.isDefault, false),
          isTop: maybe(() => pageheader.isTop, false),
          countdown: maybe(() => pageheader.countdown, false),
          pcCountdown: maybe(() => pageheader.pcCountdown, false),
          mbCountdown: maybe(() => pageheader.mbCountdown, false),
          languageOpt: maybe(() => pageheader.languageOpt, false),
          pcContent: maybe(() => pageheader.pcContent, ""),
          mobileContent: maybe(() => pageheader.mobileContent, ""),
          pcContentFormatted: maybe(() => pageheader.pcContentFormatted, "-"),
          mobileContentFormatted: maybe(
            () => pageheader.mobileContentFormatted,
            "-"
          ),
          located: maybe(() => pageheader.located, PageHeaderLocatedEnum.TOP),
          startDate: splitDateTime(maybe(() => pageheader.startDate, "")).date,
          startTime: splitDateTime(maybe(() => pageheader.startDate, "")).time,
          endDate: splitDateTime(maybe(() => pageheader.endDate, "")).date,
          endTime: splitDateTime(maybe(() => pageheader.endDate, "")).time,
          mainColor: "",
          secondaryColor: "",
          pcRollingDuration: maybe(() => pageheader.pcRollingDuration, 0),
          mbRollingDuration: maybe(() => pageheader.mbRollingDuration, 0)
        };

  const handleSubmit = (data: PageheaderPageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.pageheaders)}
          </AppHeader>
          <PageHeader
            title={
              pageheader === null
                ? intl.formatMessage({
                    defaultMessage: "Create New Pageheader",
                    description: "create new pageheader"
                  })
                : maybe(() => concat(pageheader.name))
            }
          />
          <Grid>
            <div>
              <PageheaderDetails
                errors={errors}
                data={data}
                disabled={disabled}
                onChange={change}
                title={title}
              />
              <CardSpacer />
              <PageheaderDates
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
            </div>
            <div>
              <PageheaderSummary data={data} />
            </div>
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
            onDelete={pageheader === null ? undefined : onDelete}
          />
        </Container>
      )}
    </Form>
  );
};
export default PageheaderCreatePage;
